import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { MessageService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { TreeSelectModule } from 'primeng/treeselect';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom, forkJoin } from 'rxjs';
import { afterRender } from '../../../common/after-render';
import { notifyAndTrackError } from '../../../common/error';
import { dateToIsoString, isEmpty, isStringEmpty, isStringEqual, parseLocalDate } from '../../../common/functions';
import { errorMsg, successMsg, warningMsg } from '../../../common/messages';
import { aggregateValidation, VALIDATION_PREFIX } from '../../../common/validation';
import { ProcessingComponent } from '../../../components';
import { CalendarComponent } from '../../../components/calendar/calendar.component';
import { RecurrenceScheduleComponent } from '../../../components/recurrence-schedule/recurrence-schedule.component';
import { LockInitialWidthDirective } from '../../../directives/lock-initial-width.directive';
import { AppInsightsService } from '../../../services/app-insights.service';
import {
  ContainerAdminService,
  DictionaryCacheService,
  IDictionaryEntry,
  IOrderItemAddRequest,
  IOrderItemInstanceAddRequest,
  IOrderItemInstanceUpdateRequest,
  IOrderItemType,
  IOrderItemUpdateRequest,
  IRecurrenceSchedule,
  IUnitOfMeasure,
  UnitOfMeasureAdminService
} from '../../../services/insecta';
import { OrderItemInstanceService } from '../../../services/insecta/order-item-instance.service';
import { OrderItemTypeService } from '../../../services/insecta/order-item-type.service';
import { OrderItemService } from '../../../services/insecta/orderitem.service';
import { ProfileType } from '../../../services/msgraph.service';
import { ContainerInfestationRateComponent } from '../order-insect-item/container-infestation-rate/container-infestation-rate.component';
import { InsectItemComponent } from '../order-insect-item/insect-item/insect-item.component';
import { SubstrateItemComponent } from '../order-substrate-item/substrate-item/substrate-item.component';

@Component({
  selector: 'app-order-insect-substrate',
  standalone: true,
  imports: [
    InsectItemComponent,
    ContainerInfestationRateComponent,
    DatePickerModule,
    CommonModule,
    FormsModule,
    FloatLabelModule,
    ToggleSwitchModule,
    SelectModule,
    TreeSelectModule,
    ButtonModule,
    ProcessingComponent,
    SubstrateItemComponent,
    TextareaModule,
    DialogModule,
    RecurrenceScheduleComponent,
    CalendarComponent,
    TooltipModule,
    LockInitialWidthDirective
  ],
  templateUrl: './order-insect-substrate.component.html'
})
export class OrderInsectSubstrateComponent implements OnInit {
  private readonly orderItemService = inject(OrderItemService);
  private readonly msgService = inject(MessageService);
  private readonly unitService = inject(UnitOfMeasureAdminService);
  private readonly dictionaryCache = inject(DictionaryCacheService);
  private readonly containerService = inject(ContainerAdminService);
  private readonly orderItemTypeService = inject(OrderItemTypeService);
  private readonly orderItemInstanceService = inject(OrderItemInstanceService);
  private readonly appInsights = inject(AppInsightsService);

  @ViewChild('calendarComp') calendarComp!: CalendarComponent;
  @ViewChild('recurrenceComp') recurrenceComp!: RecurrenceScheduleComponent;
  @ViewChild('projectSelect', { read: ElementRef }) projectSelect!: ElementRef;

  @Input() orderId!: string;
  @Input() catalogId!: string;
  @Input() locationId!: string;
  @Input() projects!: any[];
  @Input() catalogInformation!: any[];
  @Input() profile!: ProfileType;
  @Input() editingOrderItemDetails: any;
  @Input() editing: boolean = false;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() selectedOrderInstance: any;
  @Input() canApprove: boolean = false;
  @Input() status!: string;
  @Input() modalTitle: string = '';
  @Input() projectTooltipText: string = '';
  @Output() public dialogCloseEvent = new EventEmitter();
  @Output() public dialogCancelEvent = new EventEmitter();
  @Input() locationNodes: TreeNode[] = [];
  selectedLocationNode: TreeNode | null = null;

  units: IUnitOfMeasure[] = [];
  minDate: Date = new Date();
  maxDate: Date = new Date();
  isProcessing = false;
  unavailableDates: Date[] = [];
  unavailableDatesIsoStrings: string[] = [];
  invalidDays: number[] = [];
  invalidRecurringDates: string[] = [];
  orderItemAddRequest: IOrderItemAddRequest = {} as IOrderItemAddRequest;
  selectedCatalogInsectGrowthStageId: string | null = null;
  selectedContainerQuantityperContainer: number | null = null;
  selectedContainerStandard: boolean | true = true;

  orderInsectSubstrate: {
    container: any;
    quantity: any;
    deliveryDate: Date | null;
    note: any;
    projectId: any;
    insectItem: any;
    containerItem: any;
    substrateItem: any;
    recurring: boolean;
    deliveryLocationId?: string | null;
  } = {
    container: null,
    quantity: null,
    deliveryDate: null,
    note: null,
    projectId: null,
    insectItem: {},
    containerItem: {},
    substrateItem: {},
    recurring: false,
    deliveryLocationId: '00000000-0000-0000-0000-000000000000'
  };

  editingOrderItemInsectVariant: {
    catalogInsectVariantId: string | null;
    catalogContainerInsectGrowthStageId: string | null;
    insectPermitId: string | null;
    insectQuantity: number | null;
  } = {
    catalogInsectVariantId: null,
    catalogContainerInsectGrowthStageId: null,
    insectPermitId: null,
    insectQuantity: null
  };

  editingOrderItemContainerInfestation: {
    catalogContainerInsectGrowthStageId: string | null;
    infestationRateQuantity: number | null;
    numberOfContainers: number | null;
    standardInfestationRate: boolean | null;
  } = {
    catalogContainerInsectGrowthStageId: null,
    infestationRateQuantity: null,
    numberOfContainers: null,
    standardInfestationRate: null
  };

  editingOrderItemSubstrate: {
    catalogContainerSubstrateId: string | null;
    standardFillAmount: number | null;
    substrateAmount: number | null;
    substrateAmountUom: string | null;
    substrateQuantity: number | null;
  } = {
    catalogContainerSubstrateId: null,
    standardFillAmount: null,
    substrateAmount: null,
    substrateAmountUom: null,
    substrateQuantity: null
  };

  containerDict: IDictionaryEntry[] = [];
  substrateDict: IDictionaryEntry[] = [];
  allSubstrateLinks: any[] = [];
  filteredContainers: any[] = [];
  filteredSubstrates: any[] = [];
  substrateDisabled: boolean = true;
  containerDisabled: boolean = true;
  showRecurringDialog = false;
  selectedDates: string[] = [];
  firstDeliveryDate: Date | null = null;
  recurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  insectVarGrowSubSelected: boolean = false;
  insectSubstrateOrderItemType: any = {} as IOrderItemType;
  currentInsectGrowthStageId: string | null = null;
  currentInsectVariantId: string | null = null;
  currentSubstrateId: string | null = null;
  currentCatalogContainerInsectGrowthStageId: string | null = null;
  cancelledAfterEdit: boolean = false;
  substrateReset: boolean = false;
  insectInfestationReset: boolean = false;
  showAddButton = true;
  selectedEditingDetailsOrderInstance: any;
  leadTime: any;
  defaultRecurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  defaultSelectedDates: string[] = [];
  defaultFirstDeliveryDate: Date | null = null;
  subsetSelectedDates: string[] = [];
  subsetInstances: any[] = [];
  rescheduleInstanceDates: string[] = [];
  showMakeRecurringButton: boolean = false;
  showRecurringDatesTooltip: boolean = false;
  isInsectSubstrateFormValid: boolean = false;
  numberOfContainers: number | null = null;

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (isStringEqual(node.key!, key)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  onLocationNodeSelect(event: any): void {
    this.orderInsectSubstrate.deliveryLocationId = event.node?.key || null;
  }

  ngOnInit(): void {
    this.isProcessing = true;
    this.insectSubstrateOrderItemType = this.catalogInformation[0].OrderItemTypes.find((item: any) => item.OrderItemTypeName === 'Insect + Media');

    firstValueFrom(
      forkJoin({
        units: this.unitService.getUnitOfMeasuresAll(),
        containerDict: this.dictionaryCache.get('container'),
        substrateDict: this.dictionaryCache.get('substrate')
      })
    )
      .then(({ units, containerDict, substrateDict }) => {
        this.containerDict = containerDict;
        this.substrateDict = substrateDict;
        this.units = units.filter((u: any) => u.isActive);

        this.allSubstrateLinks = this.catalogInformation[0].Substrates || [];
        this.filteredSubstrates = this.substrateDict.filter((substrate: any) =>
          this.allSubstrateLinks.some((s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.SubstrateId, substrate.id))
        );

        this.filteredContainers = this.containerDict.filter((container: any) =>
          this.allSubstrateLinks.some((s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.ContainerId, container.id))
        );

        if (this.editing) {
          if (this.editingInstance) {
            if (isStringEqual(this.editingMode, 'single')) {
              this.showMakeRecurringButton = false;
            } else {
              if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
                this.showMakeRecurringButton = true;
              }
            }
          } else {
            this.showMakeRecurringButton = true;
          }
          console.log('Editing order item details:', this.editingOrderItemDetails);
          this.selectedEditingDetailsOrderInstance = this.editingInstance
            ? this.editingOrderItemDetails.orderItemInstances.find((inst: any) => isStringEqual(inst.id, this.selectedOrderInstance.id))
            : null;
          this.showAddButton = false;
          this.orderInsectSubstrate.note = this.editingInstance ? this.selectedEditingDetailsOrderInstance.note : this.editingOrderItemDetails.note;
          this.orderInsectSubstrate.projectId = this.editingOrderItemDetails.projectId?.toUpperCase();
          this.orderInsectSubstrate.deliveryLocationId = this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.deliveryLocationId
            : this.editingOrderItemDetails.DeliveryLocationId;
          this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.orderInsectSubstrate.deliveryLocationId!) || null;

          this.editingOrderItemInsectVariant = {
            catalogInsectVariantId: this.editingOrderItemDetails.catalogInsectVariantId,
            catalogContainerInsectGrowthStageId: this.editingOrderItemDetails.catalogContainerInsectGrowthStageId,
            insectPermitId: this.editingInstance
              ? this.selectedEditingDetailsOrderInstance.insectPermitId
              : this.editingOrderItemDetails.insectPermitId,
            insectQuantity: this.editingInstance
              ? this.selectedEditingDetailsOrderInstance.insectQuantity
              : this.editingOrderItemDetails.insectQuantity
          };
        } else {
          this.orderInsectSubstrate.deliveryLocationId = this.locationId;
          this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.locationId) || null;
          this.showMakeRecurringButton = true;
          this.isProcessing = false;
        }
      })
      .catch((err) => {
        this.isProcessing = false;
        errorMsg(this.msgService, 'Failed to load required data');
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectSubstrateComponent', action: 'InitLoad' });
      });
  }

  loadContainerTypes(): void {
    this.filteredContainers.forEach((container) => {
      firstValueFrom(this.containerService.getContainer(container.id))
        .then((details) => {
          container.containerTypeName = details.containerType?.name || 'container';
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectSubstrateComponent', action: 'LoadContainerType' });
        });
    });
  }

  getSelectedContainerTypeName(): string {
    if (!this.orderInsectSubstrate.container) {
      return 'Container';
    }
    const containerInfo = this.catalogInformation
      .flatMap((info: any) => info.Containers)
      ?.find((c: any) => c.ContainerId === this.orderInsectSubstrate.container);

    if (containerInfo) {
      return containerInfo.ContainerTypeName;
    }
    const link = this.allSubstrateLinks.find((s: any) => s.ContainerId === this.orderInsectSubstrate.container);

    return link?.ContainerTypeName || 'Container';
  }

  onClose() {
    if (this.editing) {
      this.editing = false;
      this.editingInstance = false;
      this.editingMode = '';
    }
    this.dialogCloseEvent.emit(false);
  }

  onCancel() {
    this.editing = false;
    this.editingInstance = false;
    this.editingMode = '';
    this.dialogCancelEvent.emit(false);
  }

  onReceiveInsectData(event: any): void {
    if (isEmpty(event.insectGrowthStageId) || isEmpty(event.insectVariantId)) {
      this.insectInfestationReset = true;
      this.substrateReset = true;
      this.containerDisabled = true;
      this.substrateDisabled = true;
      this.insectVarGrowSubSelected = false;
      this.orderInsectSubstrate.container = null;
      this.orderInsectSubstrate.quantity = null;
      this.orderInsectSubstrate.deliveryDate = null;
      this.orderInsectSubstrate.containerItem = {};
      this.orderInsectSubstrate.substrateItem = {};
      this.orderInsectSubstrate.recurring = false;
      this.currentCatalogContainerInsectGrowthStageId = null;
      this.removeRecurrences();
      this.computeInsectSubstrateValidation(false);
      return;
    }

    this.selectedCatalogInsectGrowthStageId = event.catalogInsectGrowthStageId;

    this.containerDisabled = false;
    const newGSId = event.insectGrowthStageId;
    const newVarId = event.insectVariantId;

    if (this.currentInsectGrowthStageId === newGSId && this.currentInsectVariantId === newVarId) {
      this.orderInsectSubstrate.insectItem = cloneDeep(event);
      this.calculateNumberOfContainers();
      this.computeInsectSubstrateValidation(false);
    } else {
      this.orderInsectSubstrate.insectItem = cloneDeep(event);
      this.currentInsectGrowthStageId = this.orderInsectSubstrate.insectItem.insectGrowthStageId;
      this.currentInsectVariantId = this.orderInsectSubstrate.insectItem.insectVariantId;

      this.insectVarGrowSubSelected = false;
      this.insectInfestationReset = true;
      this.substrateDisabled = true;
      this.orderInsectSubstrate.container = null;
      this.orderInsectSubstrate.quantity = null;
      this.orderInsectSubstrate.deliveryDate = null;
      this.orderInsectSubstrate.containerItem = {};
      this.orderInsectSubstrate.substrateItem = {};
      this.orderInsectSubstrate.recurring = false;
      this.currentCatalogContainerInsectGrowthStageId = null;
      this.removeRecurrences();

      if (this.editing) {
        this.editingOrderItemContainerInfestation = {
          catalogContainerInsectGrowthStageId: this.editingOrderItemDetails.catalogContainerInsectGrowthStageId,
          infestationRateQuantity: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.infestationRateQuantity
            : this.editingOrderItemDetails.infestationRateQuantity,
          standardInfestationRate: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.standardInfestationRate
            : this.editingOrderItemDetails.standardInfestationRate,
          numberOfContainers: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.numberOfContainers
            : this.editingOrderItemDetails.numberOfContainers
        };
      }
      this.computeInsectSubstrateValidation(false);
    }
  }

  onReceiveContainerData(event: any) {
    this.insectInfestationReset = false;
    this.orderInsectSubstrate.containerItem = cloneDeep(event);
    if (!isEmpty(event.catalogContainerInsectGrowthStageId)) {
      this.calculateNumberOfContainers();
    }

    if (this.currentCatalogContainerInsectGrowthStageId === event.catalogContainerInsectGrowthStageId) {
      this.computeInsectSubstrateValidation(false);
      return;
    }

    this.currentCatalogContainerInsectGrowthStageId = this.orderInsectSubstrate.containerItem.catalogContainerInsectGrowthStageId;
    this.insectVarGrowSubSelected = false;
    this.substrateDisabled = true;

    const catalogContainer = this.catalogInformation
      .flatMap((node: any) => node.Containers)
      .find((c: any) => c.CatalogContainerInsectGrowthStageId === event.catalogContainerInsectGrowthStageId);

    const selectedContainerId = catalogContainer?.ContainerId;
    this.orderInsectSubstrate.container = selectedContainerId;

    this.filteredSubstrates = this.substrateDict.filter((substrate: any) =>
      this.allSubstrateLinks.some(
        (s: any) =>
          isStringEqual(s.CatalogId, this.catalogId) &&
          isStringEqual(s.SubstrateId, substrate.id) &&
          isStringEqual(s.ContainerId, selectedContainerId)
      )
    );

    if (
      !isEmpty(this.orderInsectSubstrate.substrateItem.substrateId) &&
      !this.filteredSubstrates.some((s) => s.id === this.orderInsectSubstrate.substrateItem.substrateId)
    ) {
      this.orderInsectSubstrate.deliveryDate = null;
      this.currentSubstrateId = null;
      this.orderInsectSubstrate.substrateItem = {};
      this.orderInsectSubstrate.recurring = false;
      this.removeRecurrences();
    }
    if (!this.editing) {
      this.substrateReset = true;
    }
    this.substrateDisabled = false;

    if (this.editing) {
      this.editingOrderItemSubstrate = {
        catalogContainerSubstrateId: this.editingOrderItemDetails.catalogContainerSubstrateId,
        standardFillAmount: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.standardFillAmount
          : this.editingOrderItemDetails.standardFillAmount,
        substrateAmount: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateAmount
          : this.editingOrderItemDetails.substrateAmount,
        substrateAmountUom: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateAmountUom
          : this.editingOrderItemDetails.substrateAmountUom,
        substrateQuantity: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateQuantity
          : this.editingOrderItemDetails.substrateQuantity
      };
    }
    this.computeInsectSubstrateValidation(false);
  }

  private calculateNumberOfContainers(): void {
    const insectQuantity = this.orderInsectSubstrate.insectItem?.quantity;
    const infestationRate = this.orderInsectSubstrate.containerItem?.quantityPerContainer;

    if (insectQuantity != null && insectQuantity > 0 && infestationRate != null && infestationRate > 0) {
      const numContainers = Math.ceil(insectQuantity / infestationRate);
      this.numberOfContainers = numContainers;
      this.orderInsectSubstrate.containerItem.numberOfContainers = numContainers;
    } else {
      this.numberOfContainers = null;
      this.orderInsectSubstrate.containerItem.numberOfContainers = null;
    }
  }

  onReceiveSubstrateData(event: any): void {
    const newId = event.substrateId;

    if (this.currentSubstrateId === newId) {
      this.orderInsectSubstrate.substrateItem = cloneDeep(event);
      this.computeInsectSubstrateValidation(false);
    } else {
      this.orderInsectSubstrate.substrateItem = cloneDeep(event);
      this.currentSubstrateId = this.orderInsectSubstrate.substrateItem?.substrateId || null;
      this.computeInsectSubstrateValidation(false);
      if (
        this.orderInsectSubstrate.insectItem.insectVariantId &&
        this.orderInsectSubstrate.insectItem.insectGrowthStageId &&
        this.orderInsectSubstrate.substrateItem.catalogContainerSubstrateId
      ) {
        this.calcLeadTime();
      }
    }
  }

  private calcLeadTime() {
    this.isProcessing = true;
    this.removeRecurrences();
    this.insectVarGrowSubSelected = false;
    firstValueFrom(
      this.orderItemTypeService.getRecurringOrderItemTypesConstraints(
        this.catalogId.toUpperCase(),
        this.insectSubstrateOrderItemType.OrderItemTypeId,
        this.orderInsectSubstrate.insectItem.insectVariantId,
        this.orderInsectSubstrate.insectItem.insectGrowthStageId,
        this.orderInsectSubstrate.substrateItem.substrateId
      )
    )
      .then((data) => {
        this.leadTime = data.maxLeadTime;
        const DEFAULT_MIN_DATE = parseLocalDate('2000-01-01');
        const DEFAULT_MAX_DATE = new Date(2100, 11, 31);
        this.minDate = this.canApprove ? DEFAULT_MIN_DATE : data.minDate === null ? DEFAULT_MIN_DATE : parseLocalDate(data.minDate);
        this.maxDate = data.maxDate === null ? DEFAULT_MAX_DATE : parseLocalDate(data.maxDate);
        this.invalidDays = [0, 1, 2, 3, 4, 5, 6].filter((day) => !data.availableDays.includes(day));
        this.unavailableDates = data.unavailableDates.map(parseLocalDate);
        this.unavailableDatesIsoStrings = this.unavailableDates.map((date) => {
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        });
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectSubstrateComponent', action: 'CalcLeadTime' });
      })
      .finally(() => {
        if (this.editing) {
          let firstDeliveryDate: Date | null = null;
          if (this.editingInstance) {
            if (this.selectedEditingDetailsOrderInstance && this.selectedEditingDetailsOrderInstance.deliveryDate) {
              firstDeliveryDate = parseLocalDate(this.selectedEditingDetailsOrderInstance.deliveryDate);
            }
          } else {
            if (this.editingOrderItemDetails && this.editingOrderItemDetails.firstDeliveryDate) {
              firstDeliveryDate = parseLocalDate(this.editingOrderItemDetails.firstDeliveryDate);
            }
          }

          if (firstDeliveryDate && firstDeliveryDate < this.minDate && !this.canApprove) {
            warningMsg(this.msgService, 'The first delivery date for this Order Item is no longer valid and must be reselected');
            this.orderInsectSubstrate.deliveryDate = null;
            this.selectedDates = [];
            this.recurrenceSchedule = undefined;
          } else {
            this.orderInsectSubstrate.deliveryDate = firstDeliveryDate;
            if (
              (this.editingOrderItemDetails && this.editingOrderItemDetails.orderItemInstances) ||
              (this.editingInstance && this.editingMode === 'future' && this.status === 'Updated')
            ) {
              this.subsetSelectedDates = this.getSubsetSelectedDates();
              this.selectedDates = this.editingInstance
                ? this.subsetSelectedDates
                : this.editingOrderItemDetails.orderItemInstances.map((inst: { deliveryDate: any }) => inst.deliveryDate);
              this.recurrenceSchedule = this.editingOrderItemDetails.recurrenceSchedule;
              this.updateShowRecurringDatesTooltip();
            }
          }
          this.editing = false;
        } else {
          this.orderInsectSubstrate.deliveryDate = null;
        }
        this.insectVarGrowSubSelected = true;
        this.computeInsectSubstrateValidation(false);
        this.isProcessing = false;
      });
  }

  getSubsetSelectedDates(): string[] {
    if (!this.editingOrderItemDetails || !this.selectedEditingDetailsOrderInstance) {
      return [];
    }

    const selectedSeq = this.selectedEditingDetailsOrderInstance.orderItemInstanceSequence;
    this.subsetInstances = this.editingOrderItemDetails.orderItemInstances.filter((inst: any) => inst.orderItemInstanceSequence >= selectedSeq);
    return this.subsetInstances.map((inst: any) => inst.deliveryDate);
  }

  onDeliveryDateChange() {
    if (!this.editingInstance && this.selectedDates.length < 2) {
      this.selectedDates = this.orderInsectSubstrate.deliveryDate === null ? [] : [dateToIsoString(this.orderInsectSubstrate.deliveryDate)];
    }
    this.computeInsectSubstrateValidation(false);
  }

  onSaveOrderItem() {
    if (!this.computeInsectSubstrateValidation()) {
      return;
    }

    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderInsectSubstrate.substrateItem.substrateId && s.ContainerId === this.orderInsectSubstrate.container
    );

    this.isProcessing = true;
    const orderItem: IOrderItemAddRequest = {
      orderId: this.orderId,
      orderItemTypeId: this.insectSubstrateOrderItemType.OrderItemTypeId,
      projectId: this.orderInsectSubstrate.projectId || null,
      firstDeliveryDate: this.orderInsectSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderInsectSubstrate.deliveryDate),
      note: this.orderInsectSubstrate.note || null,
      actionBy: this.profile.userPrincipalName,
      catalogInsectVariantId: this.orderInsectSubstrate.insectItem.catalogInsectVariantId,
      catalogContainerInsectGrowthStageId: this.orderInsectSubstrate.containerItem.catalogContainerInsectGrowthStageId,
      insectQuantity: this.orderInsectSubstrate.insectItem.quantity,
      insectPermitId: this.orderInsectSubstrate.insectItem.permitId,
      infestationRateQuantity: this.orderInsectSubstrate.containerItem.quantityPerContainer,
      catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
      substrateAmount: this.orderInsectSubstrate.substrateItem.customAmount,
      substrateAmountUom: this.orderInsectSubstrate.substrateItem.customUnit || null,
      substrateQuantity: this.orderInsectSubstrate.containerItem.numberOfContainers,
      recurrenceSchedule: this.recurrenceSchedule,
      standardFillAmount: this.orderInsectSubstrate.substrateItem.standard,
      standardInfestationRate: this.orderInsectSubstrate.containerItem.standard,

      instances: this.buildInstanceObject()
    };

    firstValueFrom(this.orderItemService.addOrderItem(this.orderId, orderItem))
      .then(() => {
        this.isProcessing = false;
        successMsg(this.msgService, 'Order item saved successfully');
        this.onClose();
      })
      .catch((err) => {
        this.isProcessing = false;
        errorMsg(this.msgService, 'Failed to save order item');
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectSubstrateComponent', action: 'AddOrderItem' });
      });
  }

  onEditSaveOrderItem() {
    if (!this.computeInsectSubstrateValidation()) {
      return;
    }

    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderInsectSubstrate.substrateItem.substrateId && s.ContainerId === this.orderInsectSubstrate.container
    );

    this.isProcessing = true;
    if (!this.editingInstance) {
      const orderItem: IOrderItemUpdateRequest = {
        id: this.editingOrderItemDetails.id,
        orderItemTypeId: this.editingOrderItemDetails.orderItemTypeId,
        projectId: this.orderInsectSubstrate.projectId || null,
        firstDeliveryDate: this.orderInsectSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderInsectSubstrate.deliveryDate),
        note: this.orderInsectSubstrate.note || null,
        actionBy: this.profile.userPrincipalName,
        catalogInsectVariantId: this.orderInsectSubstrate.insectItem.catalogInsectVariantId,
        catalogContainerInsectGrowthStageId: this.orderInsectSubstrate.containerItem.catalogContainerInsectGrowthStageId,
        insectQuantity: this.orderInsectSubstrate.insectItem.quantity,
        insectPermitId: this.orderInsectSubstrate.insectItem.permitId,
        infestationRateQuantity: this.orderInsectSubstrate.containerItem.quantityPerContainer,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: this.orderInsectSubstrate.substrateItem.customAmount,
        substrateAmountUom: this.orderInsectSubstrate.substrateItem.customUnit || null,
        substrateQuantity: this.orderInsectSubstrate.containerItem.numberOfContainers,
        recurrenceSchedule: this.recurrenceSchedule,
        standardFillAmount: this.orderInsectSubstrate.substrateItem.standard,
        standardInfestationRate: this.orderInsectSubstrate.containerItem.standard,
        deliveryLocationId: this.orderInsectSubstrate.deliveryLocationId || null,
        instances: this.buildInstanceObject()
      };

      firstValueFrom(this.orderItemService.updateOrderItem(this.editingOrderItemDetails.orderId, orderItem))
        .then(() => {
          this.isProcessing = false;
          successMsg(this.msgService, 'Order item saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          errorMsg(this.msgService, 'Failed to save order item');
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectSubstrateComponent', action: 'UpdateOrderItem' });
        });
    }

    if (this.editingInstance) {
      this.rescheduleInstanceDates = this.selectedDates && this.selectedDates.length > 0 ? this.selectedDates : [];
      const orderItemInstance: IOrderItemInstanceUpdateRequest = {
        id: this.selectedOrderInstance.id,
        orderItemInstanceSequence: (() => {
          const seq = this.selectedOrderInstance.orderItemInstanceSequence;
          if (typeof seq === 'string') {
            const parts = seq.split('-');
            const last = parts[parts.length - 1];
            return Number(last.replace(/^0+/, ''));
          }
          return seq;
        })(),
        orderId: this.selectedOrderInstance.orderId,
        orderItemId: this.selectedOrderInstance.orderItemId,
        deliveryDate: this.orderInsectSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderInsectSubstrate.deliveryDate),
        note: this.orderInsectSubstrate.note || null,
        deliveryLocationId: this.orderInsectSubstrate.deliveryLocationId || null,
        catalogInsectVariantId: this.orderInsectSubstrate.insectItem.catalogInsectVariantId,
        catalogContainerInsectGrowthStageId: this.orderInsectSubstrate.containerItem.catalogContainerInsectGrowthStageId,
        insectQuantity: this.orderInsectSubstrate.insectItem.quantity,
        insectPermitId: this.orderInsectSubstrate.insectItem.permitId,
        infestationRateQuantity: this.orderInsectSubstrate.containerItem.quantityPerContainer,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: this.orderInsectSubstrate.substrateItem.customAmount,
        substrateAmountUom: this.orderInsectSubstrate.substrateItem.customUnit || null,
        substrateQuantity: this.orderInsectSubstrate.containerItem.numberOfContainers,
        standardFillAmount: this.orderInsectSubstrate.substrateItem.standard,
        standardInfestationRate: this.orderInsectSubstrate.containerItem.standard,
        lastEditableDate: (() => {
          if (!this.orderInsectSubstrate.deliveryDate || !this.leadTime) return undefined;
          const date = new Date(this.orderInsectSubstrate.deliveryDate);
          date.setDate(date.getDate() - (this.leadTime || 0));
          return dateToIsoString(date);
        })()
      };

      firstValueFrom(
        this.orderItemInstanceService.updateOrderItemInstance(
          this.editingOrderItemDetails.orderId,
          this.selectedOrderInstance.orderItemId,
          this.selectedOrderInstance.id,
          orderItemInstance,
          this.editingMode,
          this.status,
          this.rescheduleInstanceDates
        )
      )
        .then(() => {
          successMsg(this.msgService, 'Order instance saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          notifyAndTrackError(this.msgService, this.appInsights, err, {
            component: 'OrderInsectSubstrateComponent',
            action: 'UpdateOrderItemInstance'
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.editing = false;
          this.editingInstance = false;
          this.editingMode = '';
        });
    }
  }

  buildInstanceObject(): IOrderItemInstanceAddRequest[] {
    const datesToUse =
      this.selectedDates && this.selectedDates.length > 0
        ? this.selectedDates
        : this.orderInsectSubstrate.deliveryDate === null
          ? null
          : [dateToIsoString(this.orderInsectSubstrate.deliveryDate)];

    if (datesToUse === null || datesToUse.length === 0) {
      return [];
    }

    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderInsectSubstrate.substrateItem.substrateId && s.ContainerId === this.orderInsectSubstrate.container
    );

    return datesToUse.map((date) => {
      const deliveryDateForCalc = new Date(date);
      const lastEditableDate = new Date(deliveryDateForCalc);
      lastEditableDate.setDate(lastEditableDate.getDate() - (this.leadTime || 0));

      return {
        deliveryDate: date,
        lastEditableDate: dateToIsoString(lastEditableDate),
        note: this.orderInsectSubstrate.note || undefined,
        catalogInsectVariantId: this.orderInsectSubstrate.insectItem.catalogInsectVariantId || undefined,
        catalogContainerInsectGrowthStageId: this.orderInsectSubstrate.containerItem.catalogContainerInsectGrowthStageId || undefined,
        insectQuantity: this.orderInsectSubstrate.insectItem.quantity || undefined,
        insectPermitId: this.orderInsectSubstrate.insectItem.permitId || undefined,
        infestationRateQuantity: this.orderInsectSubstrate.containerItem.quantityPerContainer || undefined,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: this.orderInsectSubstrate.substrateItem.customAmount,
        substrateAmountUom: this.orderInsectSubstrate.substrateItem.customUnit || null,
        substrateQuantity: this.orderInsectSubstrate.containerItem.numberOfContainers || undefined,
        standardFillAmount: this.orderInsectSubstrate.substrateItem.standard,
        standardInfestationRate: this.orderInsectSubstrate.containerItem.standard,
        vendorDelay: false,
        deliveryLocationId: this.orderInsectSubstrate.deliveryLocationId || null
      };
    });
  }

  computeInsectSubstrateValidation(showToast: boolean = false): boolean {
    const insectItem = this.orderInsectSubstrate.insectItem || {};
    const containerItem = this.orderInsectSubstrate.containerItem || {};
    const substrateItem = this.orderInsectSubstrate.substrateItem || {};

    const insectGroupValid =
      (insectItem.quantity ?? 0) > 0 &&
      !isStringEmpty(insectItem.insectVariantId) &&
      !isStringEmpty(insectItem.catalogInsectGrowthStageId) &&
      !isStringEmpty(insectItem.permitId);

    const containerGroupValid =
      !isStringEmpty(containerItem.catalogContainerInsectGrowthStageId) &&
      !isEmpty(containerItem.standard) &&
      (containerItem.standard === true || (containerItem.quantityPerContainer != null && containerItem.quantityPerContainer > 0));

    const substrateGroupValid =
      !isStringEmpty(substrateItem.catalogContainerSubstrateId) &&
      (substrateItem.standard !== false || (!isStringEmpty(substrateItem.customAmount) && !isStringEmpty(substrateItem.customUnit)));

    const deliveryGroupValid = !isStringEmpty(this.orderInsectSubstrate.deliveryDate);

    const overallValid = insectGroupValid && containerGroupValid && substrateGroupValid && deliveryGroupValid;

    if (showToast && !overallValid) {
      let failingRules: { valid: boolean; message: string }[] = [];
      if (!insectGroupValid) {
        failingRules = [
          { valid: (insectItem.quantity ?? 0) > 0, message: 'Insect quantity must be greater than zero.' },
          { valid: !isStringEmpty(insectItem.insectVariantId), message: 'Insect variant is required.' },
          { valid: !isStringEmpty(insectItem.catalogInsectGrowthStageId), message: 'Insect growth stage is required.' },
          { valid: !isStringEmpty(insectItem.permitId), message: 'Permit is required.' }
        ];
      } else if (!containerGroupValid) {
        failingRules = [
          { valid: !isStringEmpty(containerItem.catalogContainerInsectGrowthStageId), message: 'Container growth stage selection is required.' },
          { valid: !isStringEmpty(containerItem.quantityPerContainer), message: 'Quantity per container is required.' },
          { valid: !isEmpty(containerItem.standard), message: 'Specify whether standard infestation rate is used.' }
        ];
      } else if (!substrateGroupValid) {
        failingRules = [
          { valid: !isStringEmpty(substrateItem.catalogContainerSubstrateId), message: 'Substrate selection is required.' },
          {
            valid: substrateItem.standard !== false || !isStringEmpty(substrateItem.customAmount),
            message: 'Custom amount is required when not using standard fill.'
          },
          {
            valid: substrateItem.standard !== false || !isStringEmpty(substrateItem.customUnit),
            message: 'Custom unit is required when not using standard fill.'
          }
        ];
      } else if (!deliveryGroupValid) {
        failingRules = [{ valid: deliveryGroupValid, message: 'Delivery date is required.' }];
      }
      aggregateValidation(this.msgService, failingRules, { prefix: VALIDATION_PREFIX, dedupe: true, silent: false });
    }

    this.isInsectSubstrateFormValid = overallValid;
    return overallValid;
  }

  openMakeRecurringDialog() {
    if (!this.orderInsectSubstrate.substrateItem.catalogContainerSubstrateId) {
      warningMsg(this.msgService, 'Please select a substrate first');
      return;
    }
    this.firstDeliveryDate = this.orderInsectSubstrate.deliveryDate ? new Date(this.orderInsectSubstrate.deliveryDate) : null;
    this.defaultRecurrenceSchedule = cloneDeep(this.recurrenceSchedule);
    this.defaultSelectedDates = cloneDeep(this.selectedDates);
    this.defaultFirstDeliveryDate = cloneDeep(this.firstDeliveryDate);
    this.showRecurringDialog = true;
  }

  onDialogShow() {
    afterRender(() => {
      if (this.calendarComp && this.calendarComp.calendarComponent) {
        this.calendarComp.calendarComponent.getApi().updateSize();
      }
    });
  }

  updateRecurrences(event: any) {
    this.selectedDates = event.recurrenceDates;
    this.invalidRecurringDates = event.invalidRecurrenceDates;
    if (event.recurrenceSchedule) {
      this.recurrenceSchedule = event.recurrenceSchedule;
    }

    if (this.selectedDates && this.selectedDates.length > 0) {
      this.orderInsectSubstrate.deliveryDate = parseLocalDate(this.selectedDates[0]);
      if (this.recurrenceSchedule && this.recurrenceSchedule.configuration) {
        this.recurrenceSchedule.configuration.customDates = this.selectedDates;
      }
    }
    this.updateShowRecurringDatesTooltip();
  }

  addRecurrences() {
    const instanceCount = this.subsetInstances.length;
    const dateCount = this.selectedDates.length;

    if (instanceCount !== dateCount && this.editingInstance && (isStringEqual(this.status, 'Updated') || isStringEqual(this.status, 'Approved'))) {
      errorMsg(
        this.msgService,
        `The number of instances to edit is ${instanceCount}, but you have selected ${dateCount} dates. The number of selected dates must match the number of instances you are editing.`
      );
    } else if (this.invalidRecurringDates.length > 0) {
      errorMsg(this.msgService, 'Invalid recurrence dates found - please correct them before continuing');
    } else {
      this.showRecurringDialog = false;
    }
    this.updateShowRecurringDatesTooltip();
    this.computeInsectSubstrateValidation(false);
  }

  removeRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp.reset();
    this.selectedDates = [];
    this.invalidRecurringDates = [];
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderInsectSubstrate.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.updateShowRecurringDatesTooltip();
    this.computeInsectSubstrateValidation(false);
  }

  cancelRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp?.reset();
    this.selectedDates = cloneDeep(this.defaultSelectedDates);
    this.recurrenceSchedule = cloneDeep(this.defaultRecurrenceSchedule);
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderInsectSubstrate.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.invalidRecurringDates = [];
    this.updateShowRecurringDatesTooltip();
    this.computeInsectSubstrateValidation(false);
  }

  private updateShowRecurringDatesTooltip() {
    if (this.editingInstance) {
      if (isStringEqual(this.editingMode, 'single')) {
        this.showRecurringDatesTooltip = false;
      } else {
        if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
          this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
        }
      }
    } else {
      this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
    }
  }

  get recurringDatesTooltip(): string {
    return this.selectedDates && this.selectedDates.length > 0 ? 'Recurring Dates:\n' + this.selectedDates.join('\n') : '';
  }
}
